import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import FileSlot from "../components/FileSlot";
import { ArrowLeft, Play, X, Plus, AlertCircle, Wand2, Link2 } from "lucide-react";

/**
 * Chain (Relay) Reconciliation Wizard — Phase A.
 *
 * Unlike the Multi-file wizard (which pins the anchor to file A forever), a
 * Chain run lets leg N use rows from the PREVIOUS leg's target file as its
 * anchor — enabling walks like PO → Invoice → Payment → Bank where the join
 * key changes every hop. Leg 1 must always use the original anchor file.
 */

const ANCHOR_SOURCE_OPTIONS = [
  { value: "original",                          label: "Use full anchor file (default)",                 group: "flat"    },
  { value: "previous_unmatched",                label: "Cascade — only unmatched anchor rows from prev hop", group: "cascade" },
  { value: "previous_unmatched_and_partials",   label: "Cascade — unmatched + partial anchor rows",       group: "cascade" },
  { value: "previous_target_matched",           label: "Relay — matched TARGET rows from prev hop (walk the chain)", group: "relay" },
  { value: "previous_target_unmatched",         label: "Relay — unmatched target rows from prev hop (find missing links)", group: "relay" },
  { value: "previous_target_all",               label: "Relay — ALL target rows from prev hop (fresh hop)", group: "relay" },
];

function bgForSource(src) {
  if (src === "original") return "transparent";
  if (src.startsWith("previous_target")) return "rgba(139,92,246,0.05)";   // purple for relay
  return "rgba(30,64,175,0.04)";                                            // blue for cascade
}
function borderForSource(src) {
  if (src === "original") return "none";
  if (src.startsWith("previous_target")) return "1px dashed rgba(139,92,246,0.35)";
  return "1px dashed rgba(30,64,175,0.3)";
}

export default function ChainReconciliationWizard() {
  const [name, setName] = useState("");
  const [anchor, setAnchor] = useState(null);
  const [anchorTsId, setAnchorTsId] = useState("");
  const [legs, setLegs] = useState([{ file: null, rule_set_id: "", transform_set_id: "", anchor_source: "original", cascade_include_partials: false, cost_column: "" }]);
  const [ruleSets, setRuleSets] = useState([]);
  const [transformSets, setTransformSets] = useState([]);
  const [cap, setCap] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Phase B/C advanced settings
  const [branchCap, setBranchCap] = useState(1);
  const [anomalyThreshold, setAnomalyThreshold] = useState(0.2);
  const [costColAnchor, setCostColAnchor] = useState("");
  const [dryRun, setDryRun] = useState(false);
  // Templates
  const [templates, setTemplates] = useState([]);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplDesc, setTplDesc] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/rule-sets/").then(r => setRuleSets(r.data || []));
    api.get("/transform-sets/").then(r => setTransformSets(r.data || [])).catch(() => {});
    api.get("/chain-runs/settings").then(r => setCap(r.data.chain_hop_cap || 10)).catch(() => {});
    api.get("/chain-templates").then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const addLeg = () => {
    if (legs.length >= cap) { setError(`Hop cap reached (${cap}).`); return; }
    setLegs([...legs, { file: null, rule_set_id: "", transform_set_id: "", anchor_source: "previous_target_matched", cascade_include_partials: false, cost_column: "" }]);
  };
  const removeLeg = (i) => setLegs(legs.filter((_, idx) => idx !== i));
  const updateLeg = (i, patch) => setLegs(legs.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const canSubmit = !!name.trim() && !!anchor && legs.length > 0 &&
                    legs.every(l => l.file && l.rule_set_id) &&
                    legs[0].anchor_source === "original";

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError("");
    try {
      const payload = {
        name: name.trim(),
        anchor_files: [
          { file_id: anchor.file_id, sheet_name: anchor.sheet || null, skip_rows: Number(anchor.skip_rows || 0) },
          ...(anchor.extra_files || []).map(ef => ({ file_id: ef.file_id, sheet_name: ef.sheet || null, skip_rows: Number(anchor.skip_rows || 0), col_mapping: ef.col_mapping || {} })),
        ],
        anchor_merge_mode: anchor.merge_mode || "superset",
        anchor_transform_set_id: anchorTsId || null,
        legs: legs.map(l => ({
          target_files: [
            { file_id: l.file.file_id, sheet_name: l.file.sheet || null, skip_rows: Number(l.file.skip_rows || 0) },
            ...(l.file.extra_files || []).map(ef => ({ file_id: ef.file_id, sheet_name: ef.sheet || null, skip_rows: Number(l.file.skip_rows || 0), col_mapping: ef.col_mapping || {} })),
          ],
          target_merge_mode: l.file.merge_mode || "superset",
          rule_set_id: l.rule_set_id,
          target_transform_set_id: l.transform_set_id || null,
          anchor_source: l.anchor_source || "original",
          cascade_include_partials: !!l.cascade_include_partials,
          cost_column: l.cost_column || null,
        })),
        branch_cap: Number(branchCap) || 1,
        anomaly_threshold: Number(anomalyThreshold) || 0.2,
        cost_column_anchor: costColAnchor || null,
        dry_run: !!dryRun,
      };
      const res = await api.post("/chain-runs/run", payload);
      if (dryRun) {
        // Show a summary alert and stay on the wizard
        const s = res.data.summary || {};
        alert(`DRY-RUN result — no data persisted.\n\n` +
              `Anchor rows: ${s.total_anchor}\nComplete: ${s.chains_complete}\nBroken: ${s.chains_broken}\nIgnored: ${s.chains_ignored}\nBranches dropped: ${s.branches_dropped}` +
              (s.anomaly_flag ? `\n\n⚠ ANOMALY: ${s.anomaly_reason}` : ""));
      } else {
        navigate(`/chain-results/${res.data.id}`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: "960px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem" }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><ArrowLeft size={18} /></button>
          <div>
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.5rem", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Link2 size={20} style={{ color: "#8B5CF6" }} /> Chain Reconciliation
            </h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", margin: 0 }}>
              Walk files in sequence: A ↔ B ↔ C ↔ D. Each hop can use a different rule set and draw its anchor from the previous hop.
            </p>
          </div>
        </div>

        <div className="recon-card" style={{ padding: "1.25rem", marginBottom: "1rem" }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: "0.35rem" }}>Run name</label>
          <input data-testid="chain-name" className="recon-input" value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. PO → Invoice → Payment → Bank 2026-02"
            style={{ width: "100%", fontSize: "0.9rem" }} />
        </div>

        <div className="recon-card" style={{ padding: "1.25rem", marginBottom: "1rem", borderLeft: "3px solid var(--sage)" }}>
          <div style={{ marginBottom: "0.5rem", fontSize: "0.72rem", fontWeight: 700, color: "var(--sage)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Hop 0 anchor (start of the chain)
          </div>
          <FileSlot file={anchor} onChange={f => setAnchor(f)}
            onClear={() => setAnchor(null)}
            label="Drop your starting file (e.g. POs, bank, ledger)" accent="var(--sage)" />
          {transformSets.length > 0 && (
            <div style={{ marginTop: "0.625rem" }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.3rem" }}>
                <Wand2 size={11} /> Transform Set (optional)
              </label>
              <select data-testid="chain-anchor-ts-select" className="recon-input" style={{ width: "100%" }}
                value={anchorTsId} onChange={e => setAnchorTsId(e.target.value)}>
                <option value="">— none —</option>
                {transformSets.map(ts => <option key={ts.id} value={ts.id}>{ts.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="recon-card" style={{ padding: "1.25rem", marginBottom: "1rem", borderLeft: "3px solid #8B5CF6" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#8B5CF6", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Hops ({legs.length}/{cap}) — each hop picks its anchor + target file + rule set
            </div>
            <button data-testid="chain-add-hop-btn" onClick={addLeg} disabled={legs.length >= cap}
              className="btn-secondary"
              style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.78rem", padding: "0.3rem 0.65rem", opacity: legs.length >= cap ? 0.5 : 1 }}>
              <Plus size={12} /> Add hop
            </button>
          </div>

          {legs.map((l, i) => (
            <div key={i} data-testid={`chain-hop-${i}`}
              style={{ marginBottom: "0.75rem", padding: i > 0 ? "0.5rem 0.75rem" : 0,
                       background: i > 0 ? bgForSource(l.anchor_source) : "transparent",
                       borderRadius: "0.5rem", border: i > 0 ? borderForSource(l.anchor_source) : "none" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "0.5rem", alignItems: "flex-start" }}>
                <FileSlot compact
                  testIdPrefix={`chain-hop-${i}`}
                  label={`Hop ${i + 1} target`}
                  accent="#8B5CF6"
                  file={l.file}
                  onChange={f => updateLeg(i, { file: f })}
                  onClear={() => updateLeg(i, { file: null })}
                />
                <div>
                  <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>Rule set for hop {i + 1}</label>
                  <select data-testid={`chain-hop-ruleset-${i}`}
                    value={l.rule_set_id} onChange={e => updateLeg(i, { rule_set_id: e.target.value })}
                    className="recon-input" style={{ width: "100%", fontSize: "0.85rem" }}>
                    <option value="">— select —</option>
                    {ruleSets.map(rs => <option key={rs.id} value={rs.id}>{rs.name}</option>)}
                  </select>
                  {transformSets.length > 0 && (
                    <div style={{ marginTop: "0.4rem" }}>
                      <label style={{ fontSize: "0.68rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "0.3rem", marginBottom: "0.2rem" }}>
                        <Wand2 size={10} /> Transform Set
                      </label>
                      <select data-testid={`chain-hop-ts-${i}`} className="recon-input"
                        style={{ width: "100%", fontSize: "0.82rem" }}
                        value={l.transform_set_id || ""} onChange={e => updateLeg(i, { transform_set_id: e.target.value })}>
                        <option value="">— none —</option>
                        {transformSets.map(ts => <option key={ts.id} value={ts.id}>{ts.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                {legs.length > 1 && (
                  <button data-testid={`chain-remove-hop-${i}`} onClick={() => removeLeg(i)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", alignSelf: "center", padding: "0.25rem" }}>
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Anchor source controls for hop 2+ */}
              {i > 0 && (
                <div data-testid={`chain-hop-anchor-src-${i}`}
                  style={{ marginTop: "0.5rem", paddingTop: "0.5rem", borderTop: "1px solid rgba(0,0,0,0.06)",
                           display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap",
                           fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                  <span style={{ fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.68rem" }}>
                    Anchor for hop {i + 1}:
                  </span>
                  <select data-testid={`chain-anchor-source-${i}`}
                    value={l.anchor_source}
                    onChange={e => updateLeg(i, { anchor_source: e.target.value, cascade_include_partials: false })}
                    className="recon-input" style={{ fontSize: "0.8rem", minWidth: "420px" }}>
                    <optgroup label="Flat / Independent">
                      <option value="original">Use full anchor file (default)</option>
                    </optgroup>
                    <optgroup label="Cascade — keep the anchor, feed leftovers">
                      <option value="previous_unmatched">{"Only unmatched rows from hop " + i}</option>
                      <option value="previous_unmatched_and_partials">{"Unmatched + partial rows from hop " + i}</option>
                    </optgroup>
                    <optgroup label="Relay — shift the anchor, walk the chain">
                      <option value="previous_target_matched">{"Matched TARGET rows from hop " + i + " (walk the chain)"}</option>
                      <option value="previous_target_unmatched">{"Unmatched target rows from hop " + i}</option>
                      <option value="previous_target_all">{"ALL target rows from hop " + i}</option>
                    </optgroup>
                  </select>
                  {l.anchor_source === "previous_unmatched" && (
                    <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", cursor: "pointer",
                                    paddingLeft: "0.5rem", borderLeft: "1px solid rgba(0,0,0,0.08)" }}
                      title="Also carry partial matches forward.">
                      <input type="checkbox"
                        data-testid={`chain-hop-${i}-include-partials`}
                        checked={!!l.cascade_include_partials}
                        onChange={e => updateLeg(i, { cascade_include_partials: e.target.checked })} />
                      + carry partials
                    </label>
                  )}
                </div>
              )}
              {i === 0 && legs.length > 1 && (
                <div style={{ marginTop: "0.35rem", fontSize: "0.7rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                  Hop 1 always uses the full anchor file.
                </div>
              )}
            </div>
          ))}
        </div>

        {error && (
          <div style={{ padding: "0.6rem 0.9rem", background: "#FEE2E2", border: "1px solid #FCA5A5", borderRadius: "0.5rem", color: "#991B1B", fontSize: "0.82rem", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {/* Phase B/C advanced settings */}
        <div className="recon-card" style={{ padding: "1rem 1.25rem", marginBottom: "1rem", borderLeft: "3px solid #6366f1" }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.5rem" }}>
            Advanced settings
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.75rem", fontSize: "0.78rem" }}>
            <div>
              <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-secondary)", display: "block" }}>Branch cap</label>
              <input type="number" min={1} max={10} value={branchCap} onChange={e => setBranchCap(e.target.value)} data-testid="chain-branch-cap"
                className="recon-input" style={{ width: "100%", fontSize: "0.85rem" }} />
              <div style={{ fontSize: "0.65rem", color: "var(--text-muted)" }}>Max chains per anchor (default 1 = 1:1)</div>
            </div>
            <div>
              <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-secondary)", display: "block" }}>Anomaly threshold</label>
              <input type="number" step={0.05} min={0} max={1} value={anomalyThreshold} onChange={e => setAnomalyThreshold(e.target.value)} data-testid="chain-anomaly-threshold"
                className="recon-input" style={{ width: "100%", fontSize: "0.85rem" }} />
              <div style={{ fontSize: "0.65rem", color: "var(--text-muted)" }}>Flag if branches_dropped / total ≥ this</div>
            </div>
            <div>
              <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-secondary)", display: "block" }}>Cost column (anchor)</label>
              <input value={costColAnchor} onChange={e => setCostColAnchor(e.target.value)} data-testid="chain-cost-col-anchor"
                placeholder="auto-detect" className="recon-input" style={{ width: "100%", fontSize: "0.85rem" }} />
              <div style={{ fontSize: "0.65rem", color: "var(--text-muted)" }}>Numeric col for $ rollup. Blank = auto</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", paddingTop: "1rem" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer", fontSize: "0.82rem" }}>
                <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} data-testid="chain-dry-run" />
                Dry run (don't persist)
              </label>
            </div>
          </div>
        </div>

        {/* Templates */}
        <div className="recon-card" style={{ padding: "1rem 1.25rem", marginBottom: "1rem", borderLeft: "3px solid #0EA5E9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#0EA5E9", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Templates
            </div>
            <select data-testid="chain-template-select" style={{ fontSize: "0.82rem", marginLeft: "auto", minWidth: "260px" }} className="recon-input"
              onChange={e => {
                const t = templates.find(x => x.id === e.target.value);
                if (!t) return;
                setBranchCap(t.branch_cap || 1);
                setAnomalyThreshold(t.anomaly_threshold || 0.2);
                setCostColAnchor(t.cost_column_anchor || "");
                setAnchorTsId(t.anchor_transform_set_id || "");
                setLegs((t.legs || []).map(l => ({
                  file: l.target_file_id ? { file_id: l.target_file_id, sheet: l.target_sheet, skip_rows: l.target_skip_rows, merge_mode: l.target_merge_mode } : null,
                  rule_set_id: l.rule_set_id, transform_set_id: l.target_transform_set_id || "",
                  anchor_source: l.anchor_source || "original", cascade_include_partials: !!l.cascade_include_partials,
                  cost_column: l.cost_column || "",
                })));
              }}>
              <option value="">— load a template —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button data-testid="chain-save-template" onClick={() => setSaveTplOpen(true)} className="btn-secondary" style={{ fontSize: "0.78rem", padding: "0.3rem 0.65rem" }}>
              Save as template
            </button>
          </div>
        </div>

        {saveTplOpen && (
          <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setSaveTplOpen(false)} />
            <div data-testid="chain-tpl-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "460px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
              <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: "0 0 0.5rem" }}>Save chain as template</h3>
              <input data-testid="chain-tpl-name" value={tplName} onChange={e => setTplName(e.target.value)} placeholder="Template name" className="recon-input" style={{ width: "100%", marginBottom: "0.5rem" }} />
              <textarea data-testid="chain-tpl-desc" value={tplDesc} onChange={e => setTplDesc(e.target.value)} placeholder="Description (optional)" className="recon-input" rows={3} style={{ width: "100%", marginBottom: "0.75rem", resize: "vertical" }} />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                <button onClick={() => setSaveTplOpen(false)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
                <button data-testid="chain-tpl-save" onClick={async () => {
                  if (!tplName.trim()) return;
                  const payload = {
                    name: tplName.trim(), description: tplDesc,
                    anchor_file_id: anchor?.file_id || null, anchor_sheet: anchor?.sheet || null, anchor_skip_rows: Number(anchor?.skip_rows || 0),
                    anchor_merge_mode: anchor?.merge_mode || "superset", anchor_transform_set_id: anchorTsId || null,
                    branch_cap: Number(branchCap) || 1, anomaly_threshold: Number(anomalyThreshold) || 0.2,
                    cost_column_anchor: costColAnchor || null,
                    legs: legs.map(l => ({
                      target_file_id: l.file?.file_id || null, target_sheet: l.file?.sheet || null, target_skip_rows: Number(l.file?.skip_rows || 0),
                      target_merge_mode: l.file?.merge_mode || "superset",
                      rule_set_id: l.rule_set_id, target_transform_set_id: l.transform_set_id || null,
                      anchor_source: l.anchor_source || "original", cascade_include_partials: !!l.cascade_include_partials,
                      cost_column: l.cost_column || null,
                    })),
                  };
                  try {
                    await api.post("/chain-templates", payload);
                    const r = await api.get("/chain-templates"); setTemplates(r.data || []);
                    setSaveTplOpen(false); setTplName(""); setTplDesc("");
                  } catch (e) { alert(e.response?.data?.detail || e.message); }
                }} className="btn-primary" style={{ fontSize: "0.85rem" }}>Save template</button>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <button onClick={() => navigate(-1)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
          <button data-testid="chain-submit" onClick={submit} disabled={!canSubmit || submitting}
            className="btn-primary"
            style={{ fontSize: "0.9rem", padding: "0.55rem 1.1rem", display: "flex", alignItems: "center", gap: "0.4rem", opacity: (!canSubmit || submitting) ? 0.5 : 1, background: "#8B5CF6", borderColor: "#8B5CF6" }}>
            {submitting ? "Running chain…" : <><Play size={14} /> Run chain reconciliation</>}
          </button>
        </div>
      </div>
    </Layout>
  );
}
